import { useState, useEffect } from "react"
import { AppState } from "../../../state/store"
import { getBookingRequest } from "./request"
import VehicleDetails from "../../../pages/VehicleDetails/VehicleDetails"

export type VehicleData = {
  Registration: string
  Mileage: number
  Condition: string
  Valuation: {
    customerValuation: number
    lookersValuation: {
      lookersExcellent: number
      lookersGood: number
      lookersAverage: number
      lookersBelow: number
    }
    preWeighting: number
    weighting: number
    capValuation: {
      retail: number
      clean: number
      average: number
      below: number
    }
  }
}

export interface DealershipData {
  AppointmentDateTime: string
  VehicleType: string
  DealershipName: string
  DealershipGuid: string
}

export type CustomerData = {
  FirstName: string
  LastName: string
  Email: string
  Mobile?: string
  Postcode: string
  Landline?: string
}

export type VehicleDetails = {
  requestResultsType: {
    asset: any
  }
}

export const useGetBooking = (
  addBookingStatus: Function,
) => {
  const [vehicleData, setVehicleData] = useState<VehicleData | null>(null)
  const [customerData, setCustomerData] = useState<CustomerData | null>(null)
  const [dealershipData, setDealershipData] = useState<DealershipData | null>(null)
  const [vehicleDetails, setVehicleDetails] = useState<VehicleDetails | null>(null)

  useEffect(() => {
    if (
      vehicleData &&
      customerData &&
      dealershipData &&
      vehicleDetails
    ) {
      // The HpiFullLookup is a field that's required in the services further down, but the data is using basic lookup
      // This will need to be changed once the services changes expect a basic lookup field
      const newVehicleData = {
        ...vehicleData,
        HpiFullLookup: vehicleDetails,
      }
      getBookingRequest(newVehicleData, customerData, dealershipData)
        .then((data) => {
          addBookingStatus(data)
        })
        .catch((error) => {
          const splitedString = error.message.split(" ")
          addBookingStatus({status: parseInt(splitedString[5]), message: error.message})
        }) 
    }
    // eslint-disable-next-line
  }, [vehicleData, customerData, dealershipData])

  const formatDateTime = (date: string, time: string) => {
    const reversed = date.split("/").reverse().join("/")
    const formatDate = reversed.replace(/\//g, "-")
    return `${formatDate}T${time}:00`
  }

  return ({
    contactDetails: { firstName, lastName, email, contactNumber, countryCode },
    appointmentDetails: { postCode, dealership },
    dealershipGuid,
    mileage,
    registration,
    vehicleCondition,
    vehicleDetails,
    bookingDate,
    bookingTime,
    valuation: {
      customerValuation,
      preWeighting,
      weighting,
      lookersValuation: {
        lookersExcellent,
        lookersAverage,
        lookersBelow,
        lookersGood,
      },
      capValuation: { retail, clean, average, below },
    },
  }: AppState) => {
    const isUkMobile = countryCode === "+44" && /^((07[0-9]{9}))$/.test(contactNumber)
    const isIrishMobile = countryCode === "+353" && /^((08[0-9]{6,13}))$/.test(contactNumber)
    const customerData = {
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      Mobile: (isUkMobile || isIrishMobile ? contactNumber : ""),
      Landline: (!isUkMobile && !isIrishMobile ? contactNumber : ""),
      Postcode: postCode,
    }
    
    setVehicleData({
      Registration: registration,
      Mileage: parseInt(mileage.replace(",", "")),
      Condition: vehicleCondition,
      Valuation: {
        customerValuation: customerValuation,
        lookersValuation: {
          lookersExcellent: lookersExcellent,
          lookersGood: lookersGood,
          lookersAverage: lookersAverage,
          lookersBelow: lookersBelow,
        },
        preWeighting: preWeighting,
        weighting: weighting,
        capValuation: {
          retail: retail,
          clean: clean,
          average: average,
          below: below,
        },
      },
    })
    setCustomerData(customerData)
    setDealershipData({
      AppointmentDateTime: formatDateTime(bookingDate, bookingTime),
      VehicleType: "Car",
      DealershipName: dealership,
      DealershipGuid: dealershipGuid,
    })
    setVehicleDetails({
      requestResultsType: {
        asset: vehicleDetails.requestResultsType.asset
      }
    })
  }
}
